import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import { useBrandTokens } from '|/brand';
import styles from '../price-details.module.css';

interface AmountRowProps {
  label: React.ReactNode;
  amount: React.ReactNode;
  /**
   * `muted` weakens only the label (title-and-registration fees);
   * `weak` weakens both (total-due breakdown) — the two expanders
   * genuinely differ.
   */
  emphasis?: 'default' | 'total' | 'muted' | 'weak' | 'discount';
  positive?: boolean;
  /** Carries the new-car ladder's sum line, when a running total follows this row. */
  ruled?: boolean;
  testId?: string;
}

export const AmountRow = ({
  label,
  amount,
  emphasis = 'default',
  positive = false,
  ruled = false,
  testId
}: AmountRowProps) => (
  <div
    className={cx(styles.row, {
      [styles.rowTotal]: emphasis === 'total',
      [styles.rowMuted]: emphasis === 'muted',
      [styles.rowWeak]: emphasis === 'weak',
      [styles.rowDiscount]: emphasis === 'discount',
      [styles.ruled]: ruled
    })}
    data-testid={testId}
  >
    <p className={styles.rowLabel}>{label}</p>
    <p className={cx(styles.rowAmount, { [styles.rowAmountPositive]: positive })}>{amount}</p>
  </div>
);

interface ExpanderRowProps {
  label: string;
  amount: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  analyticsId: string;
  testId?: string;
  /**
   * Title and registration drops its total while open because the expanded fee list
   * itemizes to exactly that number; shipping keeps its total,
   * since expanding only reveals explanatory copy.
   */
  hideAmountWhenOpen?: boolean;
  /** The total-due expander is a total row, so its label is semibold. */
  strongLabel?: boolean;
  /** Carries the new-car ladder's sum line, below the itemized list when open. */
  ruled?: boolean;
  children: React.ReactNode;
}

export const ExpanderRow = ({
  label,
  amount,
  isOpen,
  onToggle,
  analyticsId,
  testId,
  hideAmountWhenOpen = false,
  strongLabel = false,
  ruled = false,
  children
}: ExpanderRowProps) => {
  const tokens = useBrandTokens();

  return (
    <div className={cx(styles.expander, { [styles.ruled]: ruled })}>
      <button
        type="button"
        className={styles.expanderTrigger}
        onClick={onToggle}
        aria-expanded={isOpen}
        data-analytics-id={analyticsId}
        data-testid={testId}
      >
        <span className={cx(styles.expanderLabel, { [styles.expanderLabelStrong]: strongLabel })}>
          {label}
          <ChevronDown
            style={{ transform: isOpen ? 'rotate(180deg)' : undefined }}
            size={16}
            color={tokens.disclosureIcon}
            aria-hidden
          />
        </span>
        {!(isOpen && hideAmountWhenOpen) && <span className={styles.rowAmount}>{amount}</span>}
      </button>
      {isOpen && <div className={styles.expanderContent}>{children}</div>}
    </div>
  );
};
